<script setup lang="ts">
import AppContextPanel from '@/components/app-shell/AppContextPanel.vue'
import AiToolsCard from '@/components/dashboard/AiToolsCard.vue'
import { type UploadSuggestion } from '@/components/dashboard/BasisAreaTile.vue'
import BasisCard from '@/components/dashboard/BasisCard.vue'
import { type UploadOptions } from '@/components/dashboard/BasisUploadBox.vue'
import FinancingIntentCard from '@/components/dashboard/FinancingIntentCard.vue'
import FinestAssistantPanel from '@/components/FinestAssistantPanel.vue'
import FinestKpiCard from '@/components/FinestKpiCard.vue'
import { useFeatureFlagStore } from '@/stores/features'
import { Deferred, Head, usePage } from '@inertiajs/vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BankReadinessData = App.Data.Dashboard.BankReadinessData

interface PortfolioOverview {
  objectCount: number
  marketValue: number
  remainingDebt: number
  currency: string
}

const props = defineProps<{
  bankReadiness: BankReadinessData
  portfolio?: PortfolioOverview
  upload: UploadOptions
  uploadSuggestions: Record<string, UploadSuggestion>
  hasPendingImport: boolean
  welcomeMessage: { hidden: boolean }
}>()

const { t } = useI18n()
const page = usePage()
const features = useFeatureFlagStore()

const user = computed(() => page.props.auth?.user)

const welcomeTitle = computed(() =>
  user.value ? t('welcome.named', { name: user.value.first_name }) : t('welcome.default'),
)
</script>

<template>
  <Head :title="t('title')" />

  <WelcomeMessageDialog v-if="features.enabled('welcome_message')" :hidden="welcomeMessage.hidden" />
  <AppContextPanel>
    <FinestAssistantPanel :show-gauge="false" />
  </AppContextPanel>

  <div class="mb-6">
    <p class="section-label">{{ t('eyebrow') }}</p>
    <div class="flex items-center gap-2">
      <h1 class="page-title">{{ welcomeTitle }}</h1>
      <PageTitleHelp :text="t('description')" />
    </div>
  </div>

  <FinancingIntentCard class="mb-6" />

  <h2 class="mb-4 text-xl font-bold tracking-tight">{{ t('portfolio.title') }}</h2>

  <Deferred data="portfolio">
    <template #fallback>
      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Skeleton v-for="i in 4" :key="i" height="6.5rem" border-radius="1rem" />
      </div>
    </template>

    <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <FinestKpiCard
        variant="accent"
        :title="t('portfolio.objectCount')"
        :value="props.portfolio?.objectCount ?? 0"
        format="number" />
      <FinestKpiCard
        :title="t('portfolio.marketValue')"
        :value="props.portfolio?.marketValue ?? 0"
        format="currency"
        :currency="props.portfolio?.currency"
        :maximum-fraction-digits="0" />
      <FinestKpiCard
        :title="t('portfolio.remainingDebt')"
        :value="props.portfolio?.remainingDebt ?? 0"
        format="currency"
        :currency="props.portfolio?.currency"
        :maximum-fraction-digits="0" />

      <div class="border-line bg-surface-2 rounded-2xl border border-dashed p-4">
        <p class="section-label section-label--sm">{{ t('portfolio.more.title') }}</p>
        <p class="text-ink-3 mt-1 font-medium">{{ t('portfolio.more.value') }}</p>
        <p class="text-ink-3 mt-1 text-xs">{{ t('portfolio.more.hint') }}</p>
      </div>
    </div>
  </Deferred>

  <BasisCard
    :readiness="props.bankReadiness"
    :upload="props.upload"
    :upload-suggestions="props.uploadSuggestions"
    class="my-6" />

  <AiToolsCard :has-pending-import="props.hasPendingImport" class="mb-6" />
</template>

<i18n>
{
  "en": {
    "title": "Dashboard",
    "eyebrow": "Overview",
    "description": "Tell us what you want to finance. You enter your data only once – after that we use it for every further financing.",
    "welcome": {
      "default": "Welcome to FinEstHub",
      "named": "Welcome to FinEstHub, {name}"
    },
    "portfolio": {
      "title": "Portfolio overview",
      "objectCount": "Properties",
      "marketValue": "Portfolio value",
      "remainingDebt": "Remaining debt",
      "more": {
        "title": "More to come",
        "value": "Further key figures will follow",
        "hint": "Rental yield, loan-to-value, purchase price multiple"
      }
    }
  },
  "de": {
    "title": "Dashboard",
    "eyebrow": "Übersicht",
    "description": "Sag uns, was du finanzieren willst. Deine Daten trägst du nur einmal ein – danach nutzen wir sie für jede weitere Finanzierung.",
    "welcome": {
      "default": "Willkommen bei FinEstHub",
      "named": "Willkommen bei FinEstHub, {name}"
    },
    "portfolio": {
      "title": "Portfolio-Überblick",
      "objectCount": "Objekte",
      "marketValue": "Portfoliowert",
      "remainingDebt": "Restschuld",
      "more": {
        "title": "Bald mehr",
        "value": "Weitere Kennzahlen folgen",
        "hint": "Mietrendite, LTV, Kaufpreisfaktor"
      }
    }
  }
}
</i18n>
